import { v4 as uuidv4 } from "uuid";

import { API, graphqlOperation } from "aws-amplify";
import {
  timeslotByYearByWeekForCapacity,
  listEventTypesForCapacity,
} from "../graphql/reloadyard/custom-queries";
import { logError } from "./appUtils";
import { InboundStatus } from "./constants";

import { differenceInCalendarDays, format, getWeek, set } from "date-fns";
import formatInTimeZone from "date-fns-tz/formatInTimeZone";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

/**
 * Parses yard/workday-style time strings ("6:00am", "10:15pm") and applies that wall clock to `day`.
 * Shared by the schedule calendar and scheduler utilities.
 */
export const setDayFromWorkdayTimeString = (day, timeStr) => {
  if (!timeStr || typeof timeStr !== "string") {
    return set(day, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
  }
  const [rawHour, rawMinuteAmpm] = timeStr.split(":");
  let hour = parseInt(rawHour, 10);
  if (!Number.isFinite(hour)) hour = 0;
  const minute = parseInt(rawMinuteAmpm, 10);
  const ampm = String(rawMinuteAmpm || "")
    .replace(/[0-9]/g, "")
    .toLowerCase();
  if (ampm === "pm" && hour < 12) hour += 12;
  if (ampm === "am" && hour === 12) hour = 0;
  const safeMinute = Number.isFinite(minute) ? minute : 0;
  return set(day, {
    hours: hour,
    minutes: safeMinute,
    seconds: 0,
    milliseconds: 0,
  });
};

/** Sort/compare two workday time strings ("6:00am" …) using today's calendar date as anchor. */
export const compareWorkdayTimeStrings = (a, b) => {
  const ref = new Date();
  return (
    setDayFromWorkdayTimeString(ref, a) - setDayFromWorkdayTimeString(ref, b)
  );
};

/** Copies hours/minutes from `timeSource` onto the calendar date parts of `day`. */
export const applyWallClockFromDate = (day, timeSource) =>
  set(day, {
    hours: timeSource.getHours(),
    minutes: timeSource.getMinutes(),
    seconds: 0,
    milliseconds: 0,
  });

const pad2 = (n) => String(n).padStart(2, "0");

/** Hour/minute from yard workday strings ("1:00pm", "10:15am"). */
export const parseWorkdayTimeParts = (timeStr) => {
  const anchor = setDayFromWorkdayTimeString(new Date(2000, 0, 1), timeStr);
  return { hour: anchor.getHours(), minute: anchor.getMinutes() };
};

export const getIANATimezone = () => window.appUser?.reloadYard?.timeZone;

// A yard with no IANA zone is a setup error, not a normal state: every display
// helper below can then only guess the viewer's browser zone, which is wrong
// for anyone outside the yard. Warn once per session so the guess is
// diagnosable — these run on every list row and CSV cell, so warning per call
// would flood the console. The write path does not guess at all; see
// resolveScheduledDateTime.
let hasWarnedMissingTimeZone = false;
const warnMissingYardTimeZone = (scope) => {
  if (hasWarnedMissingTimeZone) return;
  hasWarnedMissingTimeZone = true;
  console.warn(
    `${scope}: reload yard has no time zone configured, falling back to the browser time zone. Set the yard time zone in Scheduling.`,
  );
};

/** Current instant as a Date whose calendar fields match the reload yard wall clock. */
export const getYardNow = () => {
  const timeZone = getIANATimezone();
  if (!timeZone) return new Date();
  return utcToZonedTime(new Date(), timeZone);
};

/**
 * One comparable form for an appointment instant, whatever shape it arrives in.
 *
 * A loaded record carries an ISO string while the scheduler hands the form a
 * Date, so `record.scheduledDateTime !== form.scheduledDateTime` compares a
 * string to an object and is true even when both name the same moment. Callers
 * use this to decide whether an appointment actually moved.
 */
export const toComparableScheduledDateTime = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

/** Format any Date/ISO value in the reload yard IANA time zone (never browser-local). */
export const formatInYardTimeZone = (date, formatStr, options) => {
  if (!date) return "";
  // date-fns throws RangeError on Invalid Date / bad IANA name. This is a
  // display helper shared by ~40 list columns and the CSV exporters — one
  // corrupt value must degrade to a bad cell (like the old DateField did),
  // never a full-page render crash or an aborted export.
  const parsed = date instanceof Date ? date : new Date(date);
  if (isNaN(parsed.getTime())) return "Invalid Date";
  const timeZone = getIANATimezone();
  if (!timeZone) {
    warnMissingYardTimeZone("dateUtils:formatInYardTimeZone");
    return format(parsed, formatStr, options);
  }
  try {
    return formatInTimeZone(parsed, timeZone, formatStr, options);
  } catch (error) {
    logError("dateUtils:formatInYardTimeZone", error);
    return format(parsed, formatStr, options);
  }
};

/** Format a yard wall-clock schedule Date without applying another timezone conversion. */
export const formatYardScheduleDate = (date, formatStr, options) => {
  if (!date) return "";
  return format(date, formatStr, options);
};

/**
 * Calendar parts for a timeslot row in the reload yard schedule calendar.
 * Schedule Date objects are held as yard wall-clock dates so date-pickers,
 * week queries, and persisted Timeslot keys all agree on the yard's day.
 */
export const getYardCalendarDateParts = (date) => ({
  year: date.getFullYear(),
  month: date.getMonth(),
  day: date.getDate(),
  week: getWeek(date),
});

export const getYardCalendarDayOfWeek = (date) => date.getDay();

export const getYardCalendarDayKey = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  return `${year}-${pad2(month + 1)}-${pad2(day)}`;
};

export const isSameYardScheduleDay = (day1, day2) => {
  if (!day1 || !day2) return false;
  return getYardCalendarDayKey(day1) === getYardCalendarDayKey(day2);
};

const formatTimeslotWallClockLabel = (
  timeslot,
  formatStr = "EEE MMM d, h:mmaaa",
) => {
  if (
    !timeslot?.time ||
    !Number.isFinite(timeslot.year) ||
    !Number.isFinite(timeslot.month) ||
    !Number.isFinite(timeslot.day)
  ) {
    return timeslot?.time || "";
  }
  const wallClock = setDayFromWorkdayTimeString(
    new Date(timeslot.year, timeslot.month, timeslot.day),
    timeslot.time,
  );
  return formatYardScheduleDate(wallClock, formatStr);
};

/** Interpret a timeslot row as UTC; returns null when yard TZ or row parts are unavailable. */
export const tryGetDateTimeFromTimeslot = (timeslot) => {
  if (!timeslot?.time) {
    return null;
  }
  const timeZone = getIANATimezone();
  if (!timeZone) {
    return null;
  }
  if (
    !Number.isFinite(timeslot.year) ||
    !Number.isFinite(timeslot.month) ||
    !Number.isFinite(timeslot.day)
  ) {
    return null;
  }

  const { hour, minute } = parseWorkdayTimeParts(timeslot.time);
  const monthHuman = timeslot.month + 1;
  const dateStr = `${timeslot.year}-${pad2(monthHuman)}-${pad2(timeslot.day)}T${pad2(hour)}:${pad2(minute)}:00`;
  return zonedTimeToUtc(dateStr, timeZone);
};

/** User-facing label for a booked timeslot (yard wall clock). */
export const formatTimeslotLabel = (timeslot, eventTypeName) => {
  if (!timeslot?.time) return "";
  const utc = tryGetDateTimeFromTimeslot(timeslot);
  const dateTimePart = utc
    ? formatInYardTimeZone(utc, "EEE MMM d, h:mmaaa")
    : formatTimeslotWallClockLabel(timeslot);
  return eventTypeName
    ? `${dateTimePart} · ${eventTypeName}`
    : dateTimePart;
};

/** Fail-fast when a timeslot must be persisted or validated for save. */
export const requireDateTimeFromTimeslot = (timeslot) => {
  const dateTime = tryGetDateTimeFromTimeslot(timeslot);
  if (dateTime && !Number.isNaN(dateTime.getTime())) {
    return dateTime;
  }
  if (!getIANATimezone()) {
    // Actionable on purpose: this fires on yards configured before the zone
    // existed, and the person hitting it is booking an appointment, not
    // administering the yard. scripts/audit-yard-time-zones.js lists the yards
    // still missing one.
    throw new Error(
      "This yard has no time zone set, so appointment times cannot be interpreted. " +
        "Set it under Reload Yard → Scheduling, then book again.",
    );
  }
  if (!timeslot?.time) {
    throw new Error("Timeslot requires a time string.");
  }
  throw new Error("Timeslot requires year, month, and day.");
};

/**
 * Timeslot → UTC instant for the write path.
 *
 * A picked timeslot is a yard wall clock, so it is only meaningful alongside
 * the yard's IANA zone. Without one there is no honest answer: guessing the
 * browser zone books the wrong hour for every user outside the yard, and
 * zonedTimeToUtc(date, null) yields an Invalid Date that reaches the record as
 * a cleared appointment. So report the misconfiguration and write nothing —
 * callers must treat null as "not scheduled".
 */
export const resolveScheduledDateTime = (timeslot, { notify, scope } = {}) => {
  try {
    return requireDateTimeFromTimeslot(timeslot);
  } catch (error) {
    logError(scope || "dateUtils:resolveScheduledDateTime", error, false);
    if (notify) notify(error.message, "error");
    return null;
  }
};

export const getDateTimeFromUtc = (date) => {
  const timeZone = getIANATimezone();
  if (!timeZone) {
    warnMissingYardTimeZone("dateUtils:getDateTimeFromUtc");
    return date ? new Date(date) : date;
  }
  return utcToZonedTime(date, timeZone);
};

export const areTimeslotsEqual = (timeslot1, timeslot2) => {
  if (!timeslot1 || !timeslot2) return false;

  const timeslot1EventTypeId = timeslot1.eventTypeId || timeslot1.eventType?.id;
  const timeslot2EventTypeId = timeslot2.eventTypeId || timeslot2.eventType?.id;

  return (
    timeslot1.year === timeslot2.year &&
    timeslot1.month === timeslot2.month &&
    timeslot1.day === timeslot2.day &&
    timeslot1.time === timeslot2.time &&
    timeslot1EventTypeId === timeslot2EventTypeId
  );
};

// Minutes since midnight (yard TZ) from a display time string like "9:00am".
export const timeStringToMinutes = (timeStr) => {
  const parsed = parseTime(timeStr);
  return parsed ? parsed.hour * 60 + parsed.minute : null;
};

// All of the yard's booked timeslots that could overlap bookings on the given
// day, fetched yard-wide via the year/week index (bookings of deactivated
// event types still occupy the yard). A booking window can cross midnight, so
// we also pull the day before and after — and, near a week boundary, the
// adjacent week's partition — deriving each week the same way the write path
// does. Rows are returned for those three calendar days; overlap is resolved
// downstream on an absolute-ms timeline (getOccupiedIntervals / isSlotBlocked).
export const fetchYardTimeslotsForDay = async ({ year, month, day }) => {
  // Distinct (year, week) partitions and calendar days covering day-1/day/day+1.
  const partitions = new Map();
  const relevantDays = new Set();
  for (const offset of [-1, 0, 1]) {
    const d = new Date(year, month, day + offset);
    partitions.set(`${d.getFullYear()}#${getWeek(d)}`, {
      year: d.getFullYear(),
      week: getWeek(d),
    });
    relevantDays.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
  }

  const items = [];
  for (const { year: pYear, week: pWeek } of partitions.values()) {
    let nextToken = null;
    do {
      const response = await API.graphql(
        graphqlOperation(timeslotByYearByWeekForCapacity, {
          reloadYardId: window.appUser?.reloadYardId,
          yearWeek: { eq: { year: pYear, week: pWeek } },
          limit: 500,
          nextToken,
        })
      );
      const page = response.data?.timeslotByYearByWeek;
      if (!page) {
        // Missing page means we can't trust the occupancy set. Fail closed
        // (throw) rather than return partial/empty data — an empty occupancy
        // set would read as "slot free" and let a double-booking through.
        throw new Error("timeslotByYearByWeek returned no data");
      }
      items.push(...(page.items ?? []));
      nextToken = page.nextToken;
    } while (nextToken);
  }
  return items.filter((row) =>
    relevantDays.has(`${row.year}-${row.month}-${row.day}`)
  );
};

// ALL event types of the yard (active AND inactive) with the hydrated asset
// cap — capacity math needs deactivated event types too.
//
// Cached briefly (and per yard) so rapid calendar navigation doesn't re-scan
// the EventType table on every click; caching the in-flight promise also
// dedupes the grid + save-time validation firing at once. The short TTL bounds
// staleness if a manager edits an event type mid-session.
const EVENT_TYPES_TTL_MS = 60000;
let eventTypesCache = { reloadYardId: null, at: 0, promise: null };

export const fetchAllEventTypes = async () => {
  const reloadYardId = window.appUser?.reloadYardId;
  const now = Date.now();
  if (
    eventTypesCache.promise &&
    eventTypesCache.reloadYardId === reloadYardId &&
    now - eventTypesCache.at < EVENT_TYPES_TTL_MS
  ) {
    return eventTypesCache.promise;
  }

  const promise = (async () => {
    const items = [];
    let nextToken = null;
    do {
      const response = await API.graphql(
        graphqlOperation(listEventTypesForCapacity, {
          filter: { reloadYardId: { eq: reloadYardId } },
          limit: 500,
          nextToken,
        })
      );
      const page = response.data?.listEventTypes;
      if (!page) {
        // Fail closed: an incomplete event-type set would corrupt the
        // capacity caps. Throw so the cache drops this rejection and the
        // next call retries, rather than caching a bad partial result.
        throw new Error("listEventTypes returned no data");
      }
      items.push(...(page.items ?? []));
      nextToken = page.nextToken;
    } while (nextToken);
    return items;
  })();

  eventTypesCache = { reloadYardId, at: now, promise };
  // Don't cache a rejection — drop it so the next call retries the fetch.
  promise.catch(() => {
    if (eventTypesCache.promise === promise) {
      eventTypesCache = { reloadYardId: null, at: 0, promise: null };
    }
  });
  return promise;
};

export const getEventTypeCap = (eventType) => {
  if (eventType.assetTypeId) {
    const cap = eventType.assetType?.maxConcurrentBookings;
    if (cap == null) {
      // Hydration bug or deleted asset — never silent. Conservative fallback.
      logError(
        "dateUtils:getEventTypeCap",
        new Error(`assetType not hydrated for eventType ${eventType.id}`)
      );
      return 1;
    }
    return cap; // 0 is valid and blocks all overlapping bookings
  }
  return eventType.numberOfResources ?? 1; // ?? — a stored 0 must stay 0
};

// All booked intervals that occupy the yard, on an absolute epoch-ms timeline
// (half-open [start, end)). Absolute timestamps — not minutes-of-day — so a
// window that crosses midnight is represented correctly and compared against
// candidates on either side of the boundary.
// Returns [{ id, start, end, cap, assetTypeId }].
export const getOccupiedIntervals = (
  rows,
  eventTypeMap,
  excludeTimeslotId = null
) => {
  const intervals = [];
  for (const row of rows) {
    if (excludeTimeslotId && row.id === excludeTimeslotId) continue;
    // A RELEASED shipment (inbound "Released" / outbound "Shipped, Released";
    // both enums share the value) has left the yard — its booking no longer
    // occupies capacity, so a new booking may take the same slot. Mirrored in
    // the server-side re-check (processInbound/processOutbound
    // validateTimeslotCapacity) — the two engines must agree or the grid
    // would offer slots that save then rejects.
    if (
      (row.inboundShipment || row.outboundShipment)?.status ===
      InboundStatus.RELEASED
    )
      continue;
    const et = eventTypeMap.get(row.eventTypeId);

    // Interval: prefer the persisted absolute window; derive from the event
    // type's current duration/buffer only for legacy rows without one.
    let start, end;
    if (row.startTime && row.endTime) {
      start = Date.parse(row.startTime);
      end = Date.parse(row.endTime);
    } else {
      if (timeStringToMinutes(row.time) === null) continue;
      // The client knows the yard timezone, so a legacy row can be anchored on
      // its own calendar day (not the candidate's) — cross-midnight-safe.
      start = tryGetDateTimeFromTimeslot(row)?.getTime();
      if (et) {
        end = start + ((et.duration ?? 0) + (et.bufferTime ?? 0)) * 60000;
      } else {
        // Orphan row (event type deleted) with no persisted interval: rare
        // bad data — log and block conservatively for one hour.
        logError(
          "dateUtils:getOccupiedIntervals",
          new Error(
            `timeslot ${row.id} references missing eventType ${row.eventTypeId}`
          )
        );
        end = start + 60 * 60000;
      }
    }
    // Skip rows with an unparseable/corrupt window rather than push NaN, which
    // would silently never match any candidate.
    if (!Number.isFinite(start) || !Number.isFinite(end)) continue;

    intervals.push({
      id: row.id,
      start,
      end,
      // Orphan rows are treated as non-asset cap 1 (maximally conservative).
      cap: et ? getEventTypeCap(et) : 1,
      assetTypeId: et?.assetTypeId || null,
    });
  }
  return intervals;
};

// Point-in-time constraint check: blocked when, at any instant of the
// candidate's window, per-asset capacity is exceeded or (when any non-asset
// booking is active) total concurrency exceeds the minimum cap in the set.
// `candidate` = { start, end, cap, assetTypeId } (assetTypeId null for
// non-asset event types).
export const isSlotBlocked = (candidate, occupied) => {
  const checkpoints = [
    candidate.start,
    ...occupied
      .filter((o) => o.start > candidate.start && o.start < candidate.end)
      .map((o) => o.start),
  ];
  for (const t of checkpoints) {
    const S = [candidate, ...occupied.filter((o) => o.start <= t && t < o.end)];

    // Constraint 1 — per-asset capacity.
    const byAsset = {};
    for (const m of S) {
      if (!m.assetTypeId) continue;
      byAsset[m.assetTypeId] = byAsset[m.assetTypeId] || {
        count: 0,
        cap: m.cap,
      };
      byAsset[m.assetTypeId].count += 1;
    }
    if (Object.values(byAsset).some((g) => g.count > g.cap)) return true;

    // Constraint 2 — most-restrictive cap, active whenever any non-asset
    // member is present in S (the candidate counts).
    if (S.some((m) => !m.assetTypeId)) {
      const minCap = Math.min(...S.map((m) => m.cap));
      if (S.length > minCap) return true;
    }
  }
  return false;
};

// Truthy return = slot is NOT available (callers treat truthy as an error).
export const validateAppointment = async (
  timeslot,
  excludeTimeslotId = null
) => {
  if (!timeslot) return false;
  // Manager override (the Scheduler's "Override Validation" switch, which only
  // a MANAGER sees): the booking is being forced in against the yard's rules on
  // purpose, capacity included. One gate here so every caller — the manager
  // save buttons, the calendar drag-drop, the carrier/customer dialogs —
  // inherits the bypass, and none of them can bypass it on their own since only
  // the scheduler stamps the flag.
  if (timeslot.schedulingOverride) return false;
  const start = timeStringToMinutes(timeslot.time);
  if (start === null) {
    // Unparseable candidate time — we can't validate it, so block
    // conservatively (truthy = not available) rather than silently pass.
    logError(
      "dateUtils:validateAppointment",
      new Error(`unparseable timeslot.time "${timeslot.time}"`)
    );
    return true;
  }

  // These two reads are independent — run them together. fetchYardTimeslotsForDay
  // derives the year#week partitions (candidate day ± 1) from the calendar date
  // internally, matching the write path.
  const [allEventTypes, rows] = await Promise.all([
    fetchAllEventTypes(),
    fetchYardTimeslotsForDay(timeslot),
  ]);
  const eventTypeMap = new Map(allEventTypes.map((et) => [et.id, et]));
  // Use the fetched copy of the candidate's event type — it has the
  // hydrated assetType.
  const candidateEventType =
    eventTypeMap.get(timeslot.eventTypeId) || timeslot.eventType;
  if (!candidateEventType) {
    // Neither the fetched set nor the hydrated timeslot carried the candidate's
    // event type — we can't size its window, so block conservatively rather
    // than crash on undefined.
    logError(
      "dateUtils:validateAppointment",
      new Error(`candidate eventType "${timeslot.eventTypeId}" not found`)
    );
    return true;
  }
  // Absolute epoch-ms window (same timeline as getOccupiedIntervals) so a
  // candidate just after midnight is compared correctly against a booking that
  // started the previous evening.
  const candidateStart = tryGetDateTimeFromTimeslot(timeslot)?.getTime();
  if (!Number.isFinite(candidateStart)) {
    logError(
      "dateUtils:validateAppointment",
      new Error(`unresolvable candidate start for time "${timeslot.time}"`)
    );
    return true;
  }
  const candidate = {
    start: candidateStart,
    end:
      candidateStart +
      ((candidateEventType.duration ?? 0) +
        (candidateEventType.bufferTime ?? 0)) *
        60000,
    cap: getEventTypeCap(candidateEventType),
    assetTypeId: candidateEventType.assetTypeId || null,
  };
  const occupied = getOccupiedIntervals(rows, eventTypeMap, excludeTimeslotId);
  return isSlotBlocked(candidate, occupied);
};

// Stamp the persisted booking window (UTC ISO) onto a lambda timeslot input
// from the event type's current duration + buffer. Written at booking time so
// later duration edits don't rewrite already-booked windows. No-op for legacy
// callers that don't carry a hydrated eventType.
const applyBookingWindow = (timeslotInput, timeslot) => {
  if (!timeslot.eventType) return;
  const startDate = tryGetDateTimeFromTimeslot(timeslot);
  // Incomplete timeslot (missing year/month/day) → persist a windowless row
  // (the lambdas handle those) instead of crashing the save mid-flight.
  if (!startDate || isNaN(startDate.getTime())) return;
  // Guard against a missing (undefined) duration/buffer, which would make the
  // arithmetic NaN and throw RangeError on toISOString(). bufferTime is the
  // realistic case — many event types have no buffer.
  const duration = timeslot.eventType.duration ?? 0;
  const bufferTime = timeslot.eventType.bufferTime ?? 0;
  timeslotInput.startTime = startDate.toISOString();
  timeslotInput.endTime = new Date(
    startDate.getTime() + (duration + bufferTime) * 60000
  ).toISOString();
};

// Validate the timeslot before persisting. An appointment is a yard wall clock,
// so a yard with no time zone cannot have one interpreted at all: this used to
// warn and let the save through on the browser's zone, which booked the wrong
// hour for every user outside the yard and left no trace on the record. Failing
// the save surfaces the misconfiguration while it can still be fixed.
const guardAppointmentTimeslot = (timeslot) => {
  requireDateTimeFromTimeslot(timeslot);
};

// reloadYardId is the partition key of the timeslotByYearByWeek GSI — a
// Timeslot persisted without it never appears in that index, so the booking
// is invisible to every capacity check (and the shipment fails to hydrate).
// Callers build timeslots from account objects whose query may not have
// selected reloadYardId, so default from the session and fail the save
// loudly rather than write an uncountable booking.
const requireTimeslotReloadYardId = (timeslot) => {
  const reloadYardId = timeslot.reloadYardId || window.appUser?.reloadYardId;
  if (!reloadYardId) {
    throw new Error("Timeslot requires a reloadYardId.");
  }
  return reloadYardId;
};

export const createLambdaAppointment = (
  timeslot,
  inboundId,
  outboundId,
  accountId,
  accountName
) => {
  guardAppointmentTimeslot(timeslot);
  const timeslotInput = {
    ...timeslot,
    id: uuidv4(),
    owner: window.appUser.owner,
    reloadYardId: requireTimeslotReloadYardId(timeslot),
    inboundId,
    outboundId,
    accountId,
    accountName,
    transactType: "create",
  };
  applyBookingWindow(timeslotInput, timeslot);
  delete timeslotInput.eventType;
  delete timeslotInput.carrier;
  delete timeslotInput.carrierUser;
  return timeslotInput;
};

// export const createAppointment = (
//   timeslot,
//   inboundId,
//   outboundId,
//   accountId,
//   accountName
// ) => {
//   debugger;
//   const timeslotInput = {
//     ...timeslot,
//     inboundId,
//     outboundId,
//     accountId,
//     accountName,
//   };
//   delete timeslotInput.eventType;

//   return API.graphql(
//     graphqlOperation(createTimeslot, { input: timeslotInput })
//   );
// };

export const updateLambdaAppointment = (timeslot) => {
  guardAppointmentTimeslot(timeslot);
  const timeslotInput = {
    ...timeslot,
    reloadYardId: requireTimeslotReloadYardId(timeslot),
    transactType: "update",
  };
  applyBookingWindow(timeslotInput, timeslot);
  delete timeslotInput.eventType;
  delete timeslotInput.carrier;
  delete timeslotInput.carrierUser;
  return timeslotInput;
};

export const deleteLambdaAppointment = (timeslotId) => {
  const timeslotInput = {
    id: timeslotId,
    transactType: "delete",
  };
  return timeslotInput;
};

export const getHoursMinutesDuration = (label, startdate, endDate) => {
  if (startdate && endDate) {
    let diffTime = Math.abs(
      new Date(endDate).valueOf() - new Date(startdate).valueOf()
    );
    // Whole minutes first: splitting fractional hours lands just under the
    // minute (8.9999…) for about half of all durations and shows one short.
    const totalMinutes = Math.floor(diffTime / (60 * 1000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) return `${label} ${hours} hours, ${minutes} minutes`;
    else return `${label} ${minutes} minutes`;
  } else if (label) return label;
  else return false;
};

// only used in app.js
// export const getIndexISODate = (date) => {
//   if (date) return new Date(date).toISOString();
//   return "";
// };

// Parse time helper function, used for events scheduling
export const parseTime = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string") return null; // Prevents parsing errors

  const match = timeStr.match(/\d+/g);
  if (!match || match.length < 2) return null; // Ensure valid format

  let [hourStr, minuteStr] = match;
  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);
  const isPM = timeStr.toLowerCase().includes("pm");

  if (isPM && hour !== 12) hour += 12;
  if (!isPM && hour === 12) hour = 0;

  return { hour, minute };
};

export const generateTimeInterval = (
  start = null,
  end = null,
  disableValue = null
) => {
  let times = [];
  const intervals = 4; // 15-minute intervals per hour
  let h = 12;
  let m = 0;
  let a = "am";

  // **(1) Default: Generate full 24-hour intervals**
  if (!start || !end) {
    for (let i = 0; i < intervals * 24; i++) {
      if (i % 4 === 0 && i > 3) h++;
      m = (i % 4) * 15;
      if (i === 48) a = "pm";
      if (h > 12) h = 1;

      times.push({
        id: `${h}:${m === 0 ? "00" : m}${a}`,
        name: `${h}:${m === 0 ? "00" : m}${a}`,
      });
    }
    return times;
  }

  // **(2) If start & end are defined, validate & parse them**
  const startTime = parseTime(start);
  const endTime = parseTime(end);
  if (!startTime || !endTime) {
    console.error("generateTimeInterval: Invalid time format.", { start, end });
    return [];
  }

  let fullDayTimes = [];
  let constrainedTimes = [];

  h = 12;
  m = 0;
  a = "am";

  // **(3) Generate full 24-hour times (needed if disableValue is set)**
  for (let i = 0; i < intervals * 24; i++) {
    if (i % 4 === 0 && i > 3) h++;
    m = (i % 4) * 15;
    if (i === 48) a = "pm";
    if (h > 12) h = 1;

    const timeObj = {
      id: `${h}:${m === 0 ? "00" : m}${a}`,
      name: `${h}:${m === 0 ? "00" : m}${a}`,
      [disableValue]: false, // Default to available
    };

    const currentMinutes = ((h % 12) + (a === "pm" ? 12 : 0)) * 60 + m;

    // **(1) Constrain valid range (INCLUSIVE) if disableValue is NOT set**
    if (!disableValue) {
      if (
        currentMinutes >= startTime.hour * 60 + startTime.minute &&
        currentMinutes <= endTime.hour * 60 + endTime.minute
      ) {
        constrainedTimes.push(timeObj); // Fully available range
      }
    }

    // **(2) Apply `disableValue` EXCLUSIVELY INSIDE the range (EXCLUSIVE)**
    if (
      disableValue &&
      currentMinutes > startTime.hour * 60 + startTime.minute && // Greater than start
      currentMinutes < endTime.hour * 60 + endTime.minute // Less than end
    ) {
      timeObj[disableValue] = true; // Inside range: mark as "not_available"
    }

    fullDayTimes.push(timeObj);
  }

  // **(4) Return the correct output**
  return disableValue ? fullDayTimes : constrainedTimes;
};

export const formatDate = (date, includeTime = false, useUTC = true) => {
  const day = useUTC
    ? String(date.getUTCDate()).padStart(2, "0")
    : String(date.getDate()).padStart(2, "0");
  const month = useUTC
    ? String(date.getUTCMonth() + 1).padStart(2, "0")
    : String(date.getMonth() + 1).padStart(2, "0");
  const year = useUTC ? date.getUTCFullYear() : date.getFullYear();

  let formattedDate = `${month}/${day}/${year}`;

  if (includeTime) {
    let hours = useUTC ? date.getUTCHours() : date.getHours();
    const minutes = useUTC
      ? String(date.getUTCMinutes()).padStart(2, "0")
      : String(date.getMinutes()).padStart(2, "0");
    const period = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    hours = String(hours).padStart(2, "0");

    formattedDate += ` ${hours}:${minutes} ${period}`;
  }

  return formattedDate;
};

export const getDayRange = (date) => {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return [startOfDay.toISOString(), endOfDay.toISOString()];
};

const getLeastAndGreatestDate = (data, key) => {
  if (!Array.isArray(data) || data.length === 0)
    return { minDate: null, maxDate: null };

  const dates = data.reduce((acc, item) => {
    const dateValue = item[key];
    if (dateValue) {
      const parsedDate = new Date(dateValue);
      if (!isNaN(parsedDate)) {
        acc.push(parsedDate);
      }
    }
    return acc;
  }, []);

  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));

  return { minDate, maxDate };
};

export const isSameDay = (day1, day2) => {
  if (!day1 || !day2) return false;
  return (
    day1.getFullYear() === day2.getFullYear() &&
    day1.getMonth() === day2.getMonth() &&
    day1.getDate() === day2.getDate()
  );
};

/**
 * Days from one event to another counted on the yard calendar with both ends
 * included: placed on the 1st and pulled on the 7th is 7 days, whatever the
 * clock times. Storage days and the demurrage clock count this way. Storage
 * billing (ChargeRuleUtils) counts the same inclusive days but on the
 * browser's calendar, so the two can differ by a day when the viewer is not
 * in the yard's zone. Null when either date is missing or unreadable.
 */
export const getYardCalendarDaysInclusive = (start, end) => {
  if (!start || !end) return null;
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;

  const timeZone = getIANATimezone();
  if (!timeZone)
    warnMissingYardTimeZone("dateUtils:getYardCalendarDaysInclusive");
  const toYardWallClock = (date) =>
    timeZone ? utcToZonedTime(date, timeZone) : date;

  return Math.max(
    0,
    differenceInCalendarDays(
      toYardWallClock(endDate),
      toYardWallClock(startDate)
    ) + 1
  );
};

/** Whether two dates fall on the same calendar day in the yard time zone. */
export const isSameYardCalendarDay = (day1, day2) => {
  if (!day1 || !day2) return false;
  const timeZone = getIANATimezone();
  if (!timeZone) return isSameDay(day1, day2);
  return (
    formatInTimeZone(day1, timeZone, "yyyy-MM-dd") ===
    formatInTimeZone(day2, timeZone, "yyyy-MM-dd")
  );
};

export const getFormattedStartAndEndDate = (
  data,
  inputStartDate,
  inputEndDate,
  key
) => {
  if (!inputStartDate || !inputEndDate) {
    const { minDate, maxDate } = getLeastAndGreatestDate(data, key);
    inputStartDate = inputStartDate || minDate;
    inputEndDate = inputEndDate || maxDate;
  }

  if (
    isNaN(new Date(inputStartDate)?.getUTCDay()) &&
    isNaN(new Date(inputEndDate)?.getUTCDay())
  ) {
    inputStartDate = new Date();
    inputEndDate = new Date();
  }

  let startDate = new Date(inputStartDate);
  let endDate = new Date(inputEndDate);
  let today = new Date();

  const localStartDate = startDate;
  const localEndDate = endDate;

  const similarDates = isSameDay(localStartDate, localEndDate);
  const isToday = isSameDay(localStartDate, today);

  const formattedStartDate = isToday
    ? formatDate(today, true, false)
    : formatDate(localStartDate);
  const formattedEndDate = similarDates ? "" : formatDate(localEndDate);

  return { formattedStartDate, formattedEndDate };
};
